/**
 * Reading money back out of a text field.
 *
 * The trader types what he said in the deal, not what a form expects, so this
 * is deliberately forgiving. The output is always integer paisa.
 */

import { CRORE, LAKH, PAISA_PER_RUPEE, rupeesToPaisa } from './format'

/** Urdu/Arabic-Indic digits, so a number pasted from a WhatsApp message works. */
const EASTERN_DIGITS = /[٠-٩۰-۹]/g

function normalizeDigits(input: string): string {
  return input.replace(EASTERN_DIGITS, (d) => {
    const code = d.charCodeAt(0)
    const base = code >= 0x06f0 ? 0x06f0 : 0x0660
    return String(code - base)
  })
}

const MULTIPLIERS: Array<[RegExp, number]> = [
  [/\b(crore|karor|karore|cr)\b\.?/i, CRORE],
  [/\b(lakh|lac|lakhs|lacs)\b\.?/i, LAKH],
  // `k\b` rather than `\bk\b`: in "45k" there is no word boundary
  // between the digit and the k.
  [/(?:\b(?:thousand|hazaar|hazar)\b|k\b)\.?/i, 1_000],
]

/**
 * Parse trader-typed input into paisa.
 *
 * Accepts: "320000", "3,20,000", "Rs 320000", "PKR 3,20,000",
 *          "3.5 lakh", "1.2 crore", "45k", and Urdu digits.
 * Returns null if nothing numeric could be found.
 *
 * Accepting "3.5 lakh" directly is a small touch that noticeably speeds up
 * data entry — it is how the amount was spoken in the deal.
 */
export function parsePKR(input: string | number | null | undefined): number | null {
  if (input === null || input === undefined) return null
  if (typeof input === 'number') return Number.isFinite(input) ? rupeesToPaisa(input) : null

  let text = normalizeDigits(input).trim()
  if (!text) return null

  const negative = /^-/.test(text) || /^\(.*\)$/.test(text)

  let multiplier = 1
  for (const [pattern, factor] of MULTIPLIERS) {
    if (pattern.test(text)) {
      multiplier = factor
      text = text.replace(pattern, ' ')
      break
    }
  }

  // Strip currency words, commas, brackets and spaces — whatever remains
  // should be a bare number.
  text = text
    .replace(/\b(rs|rs\.|pkr|rupees|rupee)\b\.?/gi, ' ')
    .replace(/[,\s()]/g, '')
    .replace(/^-/, '')

  if (!/^\d*\.?\d+$/.test(text)) return null

  const value = Number.parseFloat(text)
  if (!Number.isFinite(value)) return null

  const paisa = Math.round(value * multiplier * PAISA_PER_RUPEE)
  return negative ? -paisa : paisa
}
